import { TreeRow } from './documentTreeTypes'

export type SelectionResult = {
    next: Set<string>
    // The row that becomes the anchor for a subsequent Shift+click range.
    anchor?: string
}

// The rows in the order FlatTree renders them: depth-first over `rows` (which is already
// in render order), keeping only rows whose ancestor folders are all open. Range selection
// and Ctrl+A operate on this visible order.
export const computeVisibleRowOrder = (rows: TreeRow[], openIds: ReadonlySet<string>): string[] => {
    const rowById = new Map(rows.map(row => [row.id, row]))
    const visible: string[] = []
    for (const row of rows) {
        let ancestor = row.parentId ? rowById.get(row.parentId) : undefined
        let allOpen = true
        while (ancestor) {
            if (!openIds.has(ancestor.id)) {
                allOpen = false
                break
            }
            ancestor = ancestor.parentId ? rowById.get(ancestor.parentId) : undefined
        }
        if (allOpen) {
            visible.push(row.id)
        }
    }
    return visible
}

export const toggleSelect = (selected: ReadonlySet<string>, id: string): SelectionResult => {
    const next = new Set(selected)
    if (next.has(id)) {
        next.delete(id)
    } else {
        next.add(id)
    }
    return { next, anchor: id }
}

// Shift+click: add the visible range between the anchor and the target to the selection.
// Ranges include folders unless includeFolders is false (e.g. the task attachment picker,
// where only files make sense as attachments). Without a usable anchor it falls back to a
// plain toggle.
export const selectRange = (
    rows: TreeRow[],
    visibleOrder: string[],
    anchorId: string | undefined,
    targetId: string,
    selected: ReadonlySet<string>,
    includeFolders: boolean = true,
): SelectionResult => {
    const anchorIndex = anchorId ? visibleOrder.indexOf(anchorId) : -1
    const targetIndex = visibleOrder.indexOf(targetId)
    if (anchorIndex === -1 || targetIndex === -1) {
        return toggleSelect(selected, targetId)
    }
    const rowById = new Map(rows.map(row => [row.id, row]))
    const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
    const next = new Set(selected)
    for (const id of visibleOrder.slice(from, to + 1)) {
        if (!includeFolders && rowById.get(id)?.isFolder) {
            continue
        }
        next.add(id)
    }
    // The anchor stays put so a follow-up Shift+click extends from the same origin.
    return { next, anchor: anchorId }
}

// Ctrl+A: select every visible file (never folders).
export const selectAllVisibleFiles = (rows: TreeRow[], visibleOrder: string[]): SelectionResult => {
    const rowById = new Map(rows.map(row => [row.id, row]))
    return { next: new Set(visibleOrder.filter(id => rowById.get(id)?.isFolder === false)) }
}
